import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconSms } from '@components/icons/phone-sms';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { FieldMappingVariables } from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { formatPhoneNumber } from '@utils/strings';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { PreviewSmsProps, usePreviewSmsModal } from './preview-sms-modal';

import {
  ActionResultCard,
  EditableActionContent,
  FormSidebarFooter,
  SMSMessageCountWarning,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.send_sms;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('description')} text={action.description} />
      <SmsDetailCard action={action} />
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('sendSMS')}
        retryText={t('resendSMS')}
      />
    </Stack>
  );
};

type SMSData = {
  description: string;
  isAutomatic: boolean;
  recipients: EditorValue<{ variables?: FieldMappingVariables }>[];
  message: EditorValue<{ variables?: FieldMappingVariables }>;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm<SMSData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues() as SMSData;
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['phone'],
  });
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: SMSData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        recipients: data.recipients ?? [],
        message: data.message?.value ? data.message : {},
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            journeyId={action.journeyId}
            hideLabel
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('smsContent')}>
          <Stack space={4}>
            <Form.FieldMappingPhoneListField
              id="recipients"
              validators={['required']}
              variableGroups={phoneVariableGroups}
              label={t('recipients')}
              defaultValue={action.values?.recipients ?? []}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingTextEditor
              id="message"
              label={t('message')}
              plaintext
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.message}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <SMSMessageCountWarning message={formValues?.message} />
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

export const SmsDetailCard = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const showPreviewSmsModal = usePreviewSmsModal();

  const recipients = ((action.data?.recipients ?? []) as string[])
    .flatMap((receipient) => receipient.split(','))
    .map(formatPhoneNumber);

  const isError = isActionError(action);
  const status = getActionStatus(action);

  const progressText =
    status === 'completed'
      ? t('delivered')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notSent');

  const smsMessage: PreviewSmsProps = {
    to: recipients,
    textMessage: action.data.message ?? '',
    date: action.result?.dateSucceeded ?? undefined,
  };

  return (
    <ActionResultCard
      action={action}
      title={t('smsMessage')}
      icon={<IconSms />}
      progressText={progressText}
      errorTitle={actionErrorTitle[action.action]}
      onPreview={() => showPreviewSmsModal(smsMessage)}
    >
      <LabelTextGroup
        label={t('sendTo')}
        text={recipients}
        icon={<IconTextFormatLink color="elementPurple50" />}
      />
      <LabelTextGroup label={t('message')} text={action.data?.message ?? ''} />
    </ActionResultCard>
  );
};

const sidebarLocale = {
  en: {
    sendSMS: 'Send SMS',
    dataNotFound: 'Data not found',
    description: 'Description',
    sendTo: 'Send to',
    message: 'Message',
    inProgress: 'In progress',
    notSent: 'Not sent',
    error: 'Error',
    resendSMS: 'Resend SMS',
    recipients: 'Send to',
    smsMessage: 'SMS message',
    delivered: 'Delivered',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    smsContent: 'SMS content',
  },
};
